import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowBackIcon as ArrowBack } from 'components/common/Icon';
import { Box, Grid, Typography } from '@mui/material';
import {
  FormationsTertiaryButton,
  FormationsGhostButton,
} from 'components/common/Buttons';
import { queryClient } from 'states/reactQueryClient';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import {
  BUSINESS_DESCRIPTION_LIMIT,
  companyDetailsCustomerStep1,
  companyDetailsCustomerStep2,
  companyDetailSolePropCustomerStep1,
} from 'schemas/companyDetailsSchema';
import {
  FormationsFormActions,
  FormationsFormContainer,
  FormationsFormContent,
  FormationsFormData,
  FormationsFormFields,
  IFormField,
  renderField,
} from 'components/common/FormationsForm2';
import { UIDateFormat } from 'helpers/dateTimeFormat';
import {
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'services/account';
import { Business } from 'models/business';
import { ENTITY_MAPPING } from 'constants/common';
import {
  useUpdateCompany,
  useIndustryOptions,
  useUpdateCompanyProgress,
} from 'hooks/api';
import { formatEIN } from 'helpers/progressTracker';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatEinInput } from 'components/common/FormationsForm2/FormationsTextField';
import {
  getProgressTrackerStageStatus,
  isProgressTrackerStageCompleted,
} from 'components/ProgressTracker/Incorporation/state';
import { formatNumberWithDashes } from 'helpers/text-transformer';
import { findIndustryTitleFromCode } from 'components/IndustryOptions/helpers';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import { STATES } from '../../../enums';
import { EinFieldTitle } from './EinFieldTitle';
import { StepInputContentContainer } from '../Components/StepInputContentContainer';

const solePropStep1Fields = [
  {
    type: FormationsFormFields.Suggested,
    name: 'suggested',
    placeholder: 'Company Name',
    label: 'Company Name',
    maxLength: 80,
  },
];

interface Step1FormProps {
  businessData: Business;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const Step1Form = ({ onSubmit, businessData, onBackClick }: Step1FormProps) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const isScorp = businessData.entityType === 'S-CORP';
  const isSoleProp = businessData.entityType === ENTITY_MAPPING.sole_prop;
  const step1Fields: IFormField[] = [
    {
      type: FormationsFormFields.Text,
      name: 'name',
      label: 'Company Name',
      options: {
        placeholder: 'Company Name',
        autoFocus: true,
        maxLength: 80,
      },
    },
    {
      type: FormationsFormFields.DatePicker,
      name: 'incorporationDate',
      label: 'Date of Incorporation',
      options: {
        placeholder: 'MM/DD/YYYY',
      },
    },
    {
      type: FormationsFormFields.Text,
      name: 'ein',
      label: 'Employer Identification Number (EIN)',
      disabled,
      options: {
        maxLength: 10,
        placeholder: '12-3456789',
        formatInput: formatEinInput,
      },
    },
  ];
  const incorporatedStep1Fields = [
    ...step1Fields,
    {
      type: FormationsFormFields.CheckboxField,
      name: 'hasEIN',
      label: 'I have not received my EIN yet',
    },
  ];
  let fields: IFormField[] = incorporatedStep1Fields;
  if (isSoleProp) {
    fields = solePropStep1Fields;
  } else if (isScorp) {
    fields = step1Fields;
  }

  const onChange = (data: FormationsFormData) => {
    setDisabled(data.hasEIN);
  };

  const formInstance = useForm({
    defaultValues: {
      name: businessData?.name,
      suggested: businessData?.suggested
        ? businessData.suggested.map((item: any) => item?.name ?? item)
        : ['', '', ''],
      incorporationDate: businessData?.incorporationDate ?? null,
      ein: businessData?.ein ? formatEIN(businessData?.ein) : '',
      hasEIN: businessData?.hasEIN ?? false,
    },
    resolver: yupResolver(
      isSoleProp
        ? companyDetailSolePropCustomerStep1
        : companyDetailsCustomerStep1,
    ),
    context: { isSoleProp, isScorp },
  });
  const { handleSubmit, getValues, watch } = formInstance;
  useEffect(() => {
    onChange(getValues());
  }, [watch()]);

  return (
    <FormationsFormContainer sx={{ mt: 4 }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                  {renderField(field, formInstance)}
                </Grid>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Grid container justifyContent="space-between">
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={formInstance.formState.isSubmitting}
            >
              Back
            </FormationsGhostButton>

            <FormationsTertiaryButton
              isLoading={formInstance.formState.isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-1"
              loadingPosition="end"
            >
              Next
            </FormationsTertiaryButton>
          </Grid>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface Step2FormProps {
  businessData: Business;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const mapState = (state: { name: string; code: string }) => ({
  name: state.name,
  value: state.code,
});
const Step2Form = ({ businessData, onSubmit, onBackClick }: Step2FormProps) => {
  const { data: industryOptionsData } = useIndustryOptions();
  const isScorp = businessData.entityType === 'S-CORP';
  const isSoleProp = businessData.entityType === ENTITY_MAPPING.sole_prop;
  const fields = [
    {
      type: FormationsFormFields.AutoComplete,
      name: 'stateOfIncorporation',
      label: 'State of Incorporation',
      options: {
        placeholder: 'Washington',
        options: STATES.map(mapState),
      },
    },
    {
      type: FormationsFormFields.AutoComplete,
      name: 'industryCode',
      label: 'Business Industry',
      options: {
        placeholder: '',
        options: industryOptionsData?.map(({ title, code }) => ({
          name: title,
          value: code,
        })),
      },
    },
    {
      type: FormationsFormFields.TextArea,
      name: 'description',
      label: 'Business Description',
      options: {
        placeholder: 'Description for your business',
        characterLimit: BUSINESS_DESCRIPTION_LIMIT,
      },
    },
  ];
  const formInstance = useForm({
    defaultValues: {
      stateOfIncorporation: STATES.find(
        (item) => item.code === businessData.stateOfIncorporation,
      )?.code,
      industryCode: industryOptionsData?.find(
        (item) => item.code === businessData.industryCode,
      )?.code,
      description: businessData.description,
    },
    resolver: yupResolver(companyDetailsCustomerStep2),
    context: {
      isSoleProp,
      isScorp,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formInstance;

  return (
    <FormationsFormContainer sx={{ mt: 4 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                  {renderField(field, formInstance)}
                </Grid>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={isSubmitting}
              data-testid="back-step-2"
            >
              Back
            </FormationsGhostButton>
            <FormationsTertiaryButton
              isLoading={isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-2"
              loadingPosition="end"
            >
              Next
            </FormationsTertiaryButton>
          </Box>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface TParams {
  handleComplete: (status: ProgressTrackerStatus) => void;
  stageCompleted: boolean;
  businessData: Business;
  status: ProgressTrackerStatus | null;
  onBackClick: () => void;
  setActiveStep: (step: number) => void;
}

export const shouldShowReadOnly = (
  businessData: Business,
  status: ProgressTrackerStatus | null,
): boolean => {
  if (status === ProgressTrackerStatus.Completed) {
    return true;
  }
  const fields = [
    businessData.name,
    businessData.incorporationDate,
    businessData.stateOfIncorporation,
    businessData.industryCode,
    businessData.description,
  ];
  const allFilled = fields.every((i) => i != null);
  if (!allFilled) {
    return false;
  }
  if (status === ProgressTrackerStatus.Started && businessData.ein == null) {
    return true;
  }
  return false;
};

export const CompanyDetails = ({
  handleComplete,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  stageCompleted,
  businessData,
  status,
  onBackClick,
  setActiveStep,
}: TParams) => {
  const [formStep, setFormStep] = useState(0);
  const { t } = useTranslation();
  const isSoleProp = businessData?.entityType === ENTITY_MAPPING.sole_prop;
  const { data: industryOptionsData } = useIndustryOptions();

  const { mutateAsync: updateCompany } = useUpdateCompany({
    onSuccess: () =>
      queryClient.invalidateQueries(['business', businessData?.id]),
  });

  const onStep1Submit = async (formData: Business) => {
    const companyDetails = {
      suggestedNames: isSoleProp
        ? formData?.suggested?.filter((name) => name)
        : undefined,
      name: formData?.name ?? undefined,
      incorporationDate: formData?.incorporationDate
        ? UIDateFormat(formData?.incorporationDate)
        : undefined,
      ein: formData.ein ? formatNumberWithDashes(formData.ein) : undefined,
      hasEIN: !formData.hasEIN,
    };
    if (formData?.hasEIN) {
      companyDetails.ein = undefined;
    }
    if (businessData?.id) {
      await updateCompany({ id: businessData.id, data: companyDetails });
      setFormStep(1);
      if (status === null) {
        handleComplete(ProgressTrackerStatus.Started);
      }
    }
  };

  const onStep2Submit = async (formData: Business) => {
    const companyDetails = {
      industryCode: formData.industryCode,
      stateOfIncorporation: formData.stateOfIncorporation,
      description: formData.description,
    };
    if (businessData?.id) {
      await updateCompany({ id: businessData.id, data: companyDetails });
      const isCCorpOrLLC = [ENTITY_MAPPING.c_corp, ENTITY_MAPPING.llc].includes(
        businessData.entityType ?? '',
      );
      if (
        ProgressTrackerStatus.Completed !== status &&
        ((businessData.ein != null && isCCorpOrLLC) || !isCCorpOrLLC)
      ) {
        handleComplete(ProgressTrackerStatus.Completed);
      }
      setActiveStep(2);
    }
  };

  const onUpdateEin = () => {
    handleComplete(ProgressTrackerStatus.Completed);
  };

  const subHeading = isSoleProp
    ? t('progressTracker.incorporation.companyDetailSubHeadingSoleProp')
    : t('progressTracker.incorporation.companyDetailSubHeading');

  return (
    <div>
      <Typography variant="h5B" component="h5" data-test-id="company-details">
        Company Details
      </Typography>
      {shouldShowReadOnly(businessData, status) ? (
        <ReadOnlyForm
          items={[
            {
              key: 'company-name',
              title: 'Company Name',
              value:
                businessData.name != null && businessData?.name !== ''
                  ? businessData?.name
                  : businessData?.suggested?.map(
                      (item: any) => item?.name ?? item,
                    ) ?? '',
            },
            {
              key: 'date-of-incorporation',
              title: 'Date of Incorporation',
              value: businessData?.incorporationDate,
            },
            {
              key: 'Employer-Identification-Number-(EIN)',
              title:
                !isSoleProp && businessData.ein == null ? (
                  <EinFieldTitle
                    businessData={businessData}
                    label="Employer Identification Number (EIN)"
                    onSubmitted={onUpdateEin}
                  />
                ) : (
                  'Employer Identification Number (EIN)'
                ),
              value: businessData?.ein
                ? formatEIN(businessData?.ein)
                : undefined,
            },
            {
              key: 'State-of-Incorporation',
              title: 'State of Incorporation',
              value: businessData?.stateOfIncorporation,
            },
            {
              key: 'Business-Industry',
              title: 'Business Industry',
              value: findIndustryTitleFromCode(
                industryOptionsData ?? [],
                businessData?.industryCode,
              ),
            },
            {
              key: 'Business-Description',
              title: 'Business Description',
              value: businessData?.description,
            },
          ]}
        />
      ) : (
        <>
          <Box
            sx={{
              display: formStep === 0 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">{subHeading}</SubtitleText>
            <Step1Form
              onSubmit={onStep1Submit}
              businessData={businessData}
              onBackClick={onBackClick}
            />
          </Box>
          <Box
            sx={{
              display: formStep === 1 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">
              {t('progressTracker.incorporation.whatBusinessIndustryYouAreIn')}
            </SubtitleText>
            <Step2Form
              businessData={businessData}
              onSubmit={onStep2Submit}
              onBackClick={() => {
                setFormStep(0);
              }}
            />
          </Box>
        </>
      )}
    </div>
  );
};

interface ContainerProps {
  business: Business;
  setActiveStep: (step: number) => void;
  onBackClick: () => void;
}

export const CompanyDetailsContainer = ({
  business,
  setActiveStep,
  onBackClick,
}: ContainerProps) => {
  const { updateCompanyProgressAsync } = useUpdateCompanyProgress(
    business?.id,
  );

  const handleComplete = async (status: ProgressTrackerStatus) => {
    const requestProps: ProgressTrackerGroupRequest = {
      group: ProgressTrackerGroups.Incorporation,
      status,
      stage: ProgressTrackerStages.CompanyDetails,
    };

    await updateCompanyProgressAsync({
      progress: [requestProps],
      eventData: {
        stage: ProgressTrackerStages.CompanyDetails,
        stageStatus: status,
        entityType: business?.entityType ?? '',
      },
    });
  };

  const stateCompleted = isProgressTrackerStageCompleted(
    business ?? null,
    ProgressTrackerStages.CompanyDetails,
  );
  const status = getProgressTrackerStageStatus(
    business ?? null,
    ProgressTrackerStages.CompanyDetails,
  );

  return (
    <CompanyDetails
      handleComplete={handleComplete}
      status={status ?? null}
      stageCompleted={stateCompleted}
      businessData={business}
      onBackClick={onBackClick}
      setActiveStep={setActiveStep}
    />
  );
};
